// Field Labels rev 2.8: the site as rooms and gear, built with taps. Pure: no database, no network, no DOM.
// Words: FIELD_LABELS_WORDS.md sections 14 to 21. Mockup: MOCKUP_FIELD_LABELS_REV2.html rev 2.8 (approved 2026-10-23).
//
// The gear is the record. Every label is computed from it, so one fix changes every label it touches:
// the room view, the print preview and the PDFs (section 21).
// field-labels.ts stays the engine underneath: sizes, the fit rule, print files by paper size, the PDF.
//
// Rules settled 2026-10-23 (HANDOFF_FIELD_LABELS_I15.md, do not re-decide):
// * Only MCCs share the alphabet in a room. MCC-1A takes A, B; MCC-1B picks up at C. SWBD and SWGR each start at A.
// * One MCC in a room is MCC-01. A second renames it MCC-1A and adds MCC-1B. Removing one renames the rest by the same rule.
// * Other gear counts up per room: PNL-01, PNL-02.
// * Spares still print their label. The mark is data for the creator and the Map.
// * Room names carry into the creator as Location + Loc # (ER1A is Loc # 1A).

import {
  BIG_TYPES,
  BREAKER_TYPES,
  fitProblems,
  isSectioned,
  kidsKey,
  parseLineups,
  sectionCounts,
  sizeFromKey,
  type FieldLabel,
  type Housing,
  type Lineup,
  type Problem,
  type Room,
  type SizeChoices,
} from "./field-labels";
import { photoDriveName } from "./drive-folders";

// ---- The record ----

/**
 * A room or gear photo (ROOM_GEAR_PHOTO_B5_WORDS.md, mockup approved 2026-10-29 5:19 AM). One per room, one per gear.
 * The picture itself waits on the iPad in the Outbox under path and goes to Drive on signal. It rides on the room or
 * the gear, so it moves with a re-code, a rename and a drag. Undo and Cancel never take a photo back (carryPhotos).
 */
export interface WalkPhoto {
  /** One photo, kept through every send of it. A Retake is a new photo with a new id. */
  id: string;
  /** When it was taken. The newer photo wins. */
  at: string;
  /** The Storage path of its last send, also its key in the Outbox on this iPad. */
  path: string;
  /** The Drive name it was last sent under: 00_ER2.jpg. A re-code or rename sends it again. */
  sent: string;
}

export interface Gear {
  /** Stable while names change (MCC-01 becomes MCC-1A). */
  id: string;
  /** "MCC", "PNL", "XFMR". */
  type: string;
  name: string;
  /** Sectioned gear: spaces per section, left to right. */
  sections: number[];
  /** Sectioned gear: a space split 2, 3 or 4 ways. Key "section:space" (0 up : 1 up), so letters can move without losing it. */
  splits: Record<string, number>;
  /** Sectioned gear: spaces marked spare, same keys as splits. */
  spares: string[];
  /** Panels: a breaker's top circuit and its poles. Spaces are skipped. */
  circuits: Record<string, number>;
  /** Panels: circuits the panel is built for. */
  panelSize: number;
  main: boolean;
  /**
   * Panels: print B1 to Bn too. Off (missing) by default: a panel prints its housing label and MB, and B1 to Bn
   * stay in the record (Robert, 2026-10-27, 3:37 PM and 9:19 PM; PANEL_PRINT_B1_WORDS.md).
   */
  breakerLabels?: boolean;
  /** A subpanel: the gear ID of the panel it is lettered off (PNL-1A, Subpanel Of PNL-01). Decision 2, 2026-10-27. */
  parentId?: string;
  /** Sites Equipment Tag: the plant's own name for it (PL3, LVSP2). Never prints on the field label. */
  tag?: string;
  /** The gear's overview photo, door open (PHASE2_OPEN_B1_WORDS.md item 5, closed 2026-10-28 4:56 PM). */
  photo?: WalkPhoto;
  /** Set by Rename: the name stays when the room is ordered, and the count skips it (B3, 2026-10-28). */
  named?: true;
  /**
   * Shock Only · 120 V (Robert, 2026-10-28 10:52 AM): fed over 50 V and under 208 V. It takes a shock label in place
   * of the arc flash work: no field label, no QR label. Logged on the print card; Bring In sets 120V (1-P).
   */
  shockOnly?: true;
  /** Panels: the main at the bottom, drawn below the grid and printed last (decision 1, 12:56 PM). Missing: top. */
  mainAt?: "bottom";
  /**
   * Panels: the main sits in the grid, on the breaker with this top circuit. It reads MB and the feeders count around
   * it (MAIN_IN_GRID_B3_WORDS.md, Robert 2026-10-28 7:24 AM). Set only while `main` is true.
   */
  mainCircuit?: number;
  /** Panels: circuits with a filler installed (F3a). No number, no label. */
  blanks?: number[];
  /** Panels: 1-pole positions holding a tandem, two handles in one space: B5A on top, B5B below (Robert, 9:24 PM). */
  tandems?: number[];
  /**
   * Panels: breakers past the panel size, set aside when the size went down, so a size back up brings them back
   * (field test 2026-10-28 2:39 PM: 42 to 24 and back lost every breaker). Never drawn, printed or counted.
   */
  parked?: { circuits: Record<string, number>; tandems: number[]; blanks: number[]; mainCircuit?: number };
  /** Sectioned gear: fixed Covers by section index, [on top, at bottom] (decision 1). No number, no label. */
  covers?: Record<string, [number, number]>;
  /** A size picked in the field for the gear's own label ("1x4"). Empty: the default. */
  size: string;
  /** A size picked for every bucket or breaker inside it. Empty: the default. */
  kidSize: string;
}

export interface SiteRoom {
  /** "ER1", "ER1A", "EA2", "Outside". */
  name: string;
  gear: Gear[];
  /** Marked done by Room Done (PUSH_BY_ROOM_B7_WORDS.md item 5; was Done With ER1). A mark, never a lock. It gates the room's push. */
  done?: boolean;
  /** The plant's own name for the room: Room B, Mech Room (2026-10-27, 4:04 PM). Never prints. */
  description?: string;
  /** The room photo, taken last, once the room is settled (Robert, 2026-10-28 5:17 PM). */
  photo?: WalkPhoto;
}

export type RoomLight = "gray" | "yellow" | "green";

/** The walk tracker, in the survey's colors: gray nothing yet, yellow gear started, green done. */
export function roomLight(r: SiteRoom): RoomLight {
  return r.done ? "green" : r.gear.length ? "yellow" : "gray";
}

export interface FieldSite {
  rooms: SiteRoom[];
  /**
   * What each label looked like when it last printed, by its print ID (ONE_PRINT_WORDS.md, 2026-10-24).
   * Kept inside the walk's text, so rule 36 needs no change. Undo never takes it back: printing is not an edit.
   */
  printed?: Record<string, PrintedMark>;
  /**
   * Each Drive PDF's label order when it was last made, by roll (its size key), as print IDs (field test
   * 2026-10-28 4:23 PM). A move that renames nothing still shows the PDF behind. Like printed, never undone.
   */
  sent?: Record<string, string[]>;
  /**
   * Each room's last push to the Assessment Sheet, by room name (PUSH_BY_ROOM_B7_WORDS.md items 8 and 9): when, and
   * the Asset IDs the room made then. Green ON SHEET reads it, so every iPad shows the same pills. Like printed, never
   * undone: a push is not an edit. A room recoded or removed leaves its mark behind; the next push drops it.
   */
  pushed?: Record<string, PushMark>;
}

export interface PushMark {
  at: string;
  ids: string[];
}

export interface PrintedMark {
  text: string;
  at: string;
}

export const PANEL_SIZES: readonly number[] = [12, 20, 24, 30, 42, 54, 84];
export const DEFAULT_PANEL_SIZE = 42;
export const SPLIT_LETTERS = ["a", "b", "c", "d"] as const;
export const OUTSIDE = "Outside";
/** ER and EA only (Robert, 2026-10-27, 4:04 PM). EL came out: the workbook, the Creator and the rules never had it. */
export const ROOM_KINDS_REV2 = ["ER", "EA", "OUT"] as const;
export type RoomKindRev2 = (typeof ROOM_KINDS_REV2)[number];
export const ROOM_PARTS: readonly string[] = ["", "A", "B", "C"];

/** Quick types on the left, the common gear first. The rest are under More. */
export const QUICK_INSIDE: readonly string[] = ["MCC", "PNL", "SWBD", "SWGR", "ENCL", "VFD", "XFMR", "DS", "JBOX"];
/** Outside leads with the service: utility, transformer, generator, disconnect, transfer switch. */
export const QUICK_OUTSIDE: readonly string[] = ["UTL", "XFMR", "GEN", "DS", "ATS", "ENCL", "PNL", "JBOX", "VFD"];

export const isSec = (g: Pick<Gear, "type">): boolean => isSectioned(g.type);
export const isPanel = (g: Pick<Gear, "type">): boolean => BREAKER_TYPES.includes(g.type);
export const spaceKey = (section: number, space: number): string => `${section}:${space}`;
/** A to Z. Past Z there is no letter: "?" shows on the square and a red line says so. */
const letterOf = (n: number): string => (n < 26 ? String.fromCharCode(65 + n) : "?");

let idSeq = 0;
export function newGearId(): string {
  idSeq += 1;
  return `g${Date.now().toString(36)}${idSeq.toString(36)}`;
}

export function blankGear(type: string, name: string, id = newGearId()): Gear {
  return { id, type, name, sections: [], splits: {}, spares: [], circuits: {}, panelSize: DEFAULT_PANEL_SIZE, main: false, size: "", kidSize: "" };
}

// ---- Rooms ----

/** ER before EA, then number, then part. Outside last. */
export function roomOrder(name: string): number {
  if (name === OUTSIDE) return 99999;
  const m = /^(ER|EA)(\d+)([A-Z]?)$/.exec(name);
  return m ? ["ER", "EA"].indexOf(m[1]!) * 10000 + Number(m[2]) * 10 + (m[3] ? m[3].charCodeAt(0) - 64 : 0) : 50000;
}

export function sortRooms(site: FieldSite): void {
  site.rooms.sort((a, b) => roomOrder(a.name) - roomOrder(b.name));
}

/** The names the Rooms sheet would add: ER1A, ER2A from ER, numbers 1 and 2, part A. Outside has no number. */
export function roomNames(kind: RoomKindRev2, nums: readonly number[], part: string): string[] {
  if (kind === "OUT") return [OUTSIDE];
  return [...nums].sort((a, b) => a - b).map((n) => `${kind}${n}${part}`);
}

/** Adds rooms that are not there yet. Rooms stay, empty or not. Returns the names added. */
export function addRooms(site: FieldSite, names: readonly string[]): string[] {
  const added = names.filter((n) => !site.rooms.some((r) => r.name === n));
  for (const n of added) site.rooms.push({ name: n, gear: [] });
  sortRooms(site);
  return added;
}

export interface RoomRename {
  from: string;
  to: string;
}

/** The name a room takes as kind: the same number and part when free, else the next free number above it. */
function freeName(site: FieldSite, kind: "ER" | "EA", num: number, part: string, except: string): string {
  const taken = (n: string) => site.rooms.some((r) => r.name === n && r.name !== except);
  let n = num;
  while (taken(`${kind}${n}${part}`)) n++;
  return `${kind}${n}${part}`;
}

function renameRoom(site: FieldSite, from: string, to: string): void {
  const r = site.rooms.find((x) => x.name === from);
  if (r) r.name = to;
}

/**
 * The re-code (Robert, 2026-10-27, 4:07 PM): each EL room becomes EA with the same number and part, EL4 to EA4.
 * Taken: the next free EA number. Gear, breakers and Done marks stay. Lowest EL first, so EL1 claims EA1 before EL2.
 * Printed marks are keyed by gear, never by room (printIdOf), so nothing printed reads NEW or CHANGED after it.
 */
export function recodeElRooms(site: FieldSite): RoomRename[] {
  const els = site.rooms
    .map((r) => ({ r, m: /^EL(\d+)([A-Z]?)$/.exec(r.name) }))
    .filter((x): x is { r: SiteRoom; m: RegExpExecArray } => Boolean(x.m))
    .sort((a, b) => Number(a.m[1]) - Number(b.m[1]) || a.m[2]!.localeCompare(b.m[2]!));
  const out: RoomRename[] = [];
  for (const { r, m } of els) {
    const to = freeName(site, "EA", Number(m[1]), m[2]!, r.name);
    out.push({ from: r.name, to });
    r.name = to;
  }
  if (out.length) sortRooms(site);
  return out;
}

/** What Change To would make of a room: ER1 to EA1, EA4 to ER4 (or the next free number). Null for Outside. */
/** The code a room can take: ER or EA, 1 to 12, and a part when it has one (ER2, EA4B). */
const ROOM_CODE = /^(ER|EA)([1-9]|1[0-2])([A-C]?)$/;

/**
 * Why a room cannot take that code, "" when it already has it, or null when it is good to go
 * (Robert, 2026-10-28 5:39 AM: tap ER6, pick ER2, Change To ER2).
 */
export function recodeProblem(site: FieldSite, from: string, to: string): string | null {
  if (from === OUTSIDE) return "Outside has no number.";
  if (!ROOM_CODE.test(to)) return "Use ER or EA, a number 1 to 12, and a part if it has one.";
  if (to === from) return "";
  if (site.rooms.some((r) => r.name === to)) return `${to} is taken.`;
  return null;
}

/** Change ER6 To ER2: the room keeps its gear, its Done mark and its description, and the list re-sorts. */
export function recodeRoom(site: FieldSite, from: string, to: string): boolean {
  if (recodeProblem(site, from, to) !== null || !site.rooms.some((r) => r.name === from)) return false;
  renameRoom(site, from, to);
  sortRooms(site);
  return true;
}

/** The next room on the walk: the next one in order, else the first one still empty. Null: pick from the Rooms sheet. */
export function nextRoom(site: FieldSite, cur: string): string | null {
  const i = site.rooms.findIndex((r) => r.name === cur);
  return site.rooms[i + 1]?.name ?? site.rooms.find((r) => !r.gear.length && r.name !== cur)?.name ?? null;
}

/** "ER1A" as the creator reads it: Location ER, Loc # 1A. Outside is UTL with no number. */
export function locationOf(room: string): { location: string; locNumber: string } {
  const m = /^(ER|EA)(\d+[A-Z]?)$/.exec(room);
  return m ? { location: m[1]!, locNumber: m[2]! } : { location: room === OUTSIDE ? "UTL" : room, locNumber: "" };
}

// ---- Names ----

/**
 * The name new gear of a type gets in a room. One MCC is MCC-01; a second renames MCC-01 to MCC-1A and comes in as
 * MCC-1B. The rest count up from the lowest free number: PNL-01, PNL-02.
 */
export function addGear(room: SiteRoom, type: string, id = newGearId()): Gear {
  const g = blankGear(type, "", id);
  room.gear.push(g);
  if (type === "MCC") renameMccs(room);
  else {
    const nums = room.gear.filter((x) => x.type === type && x !== g).map((x) => Number(/-(\d+)$/.exec(x.name)?.[1] ?? 0));
    let n = 1;
    while (nums.includes(n)) n++;
    g.name = `${type}-${String(n).padStart(2, "0")}`;
  }
  return g;
}

/** The lockout rule, applied after any MCC comes in or out: one is MCC-01, two or more are MCC-1A, 1B, 1C in hanging order. */
export function renameMccs(room: SiteRoom): void {
  const m = room.gear.filter((g) => g.type === "MCC");
  m.forEach((g, i) => (g.name = m.length === 1 ? "MCC-01" : `MCC-1${letterOf(i)}`));
}

export function removeGear(room: SiteRoom, id: string): void {
  const i = room.gear.findIndex((g) => g.id === id);
  if (i < 0) return;
  const [g] = room.gear.splice(i, 1);
  // Its subpanels stay, as plain panels, named as they are (FIELD_NAMING_B1_WORDS.md decision 2).
  for (const k of room.gear)
    if (k.parentId === id) {
      delete k.parentId;
      k.named = true;
    }
  // What is left counts again from 01 in hanging order, the same as a drop (field test 2026-10-28 2:51 PM: delete
  // ENCL-01 and 02 of four, the last two read ENCL-01 and 02). Rename names stay. Printed labels read Changed.
  if (g) nameInOrder(room);
}

// ---- Subpanels, order and names (FIELD_NAMING_B1_WORDS.md) ----

export const ROOM_DESCRIPTION_MAX = 60;
export const GEAR_TAG_MAX = 40;
/** Same rule as the Creator's Sites Equipment Tag (asset-id.ts tagText): spaces squeezed, trimmed, capped. */
export const cleanText = (t: string, max: number): string => String(t ?? "").replace(/\s+/g, " ").trim().slice(0, max);

/** "PNL-01" gives "1"; "PNL-2A" and "ODD" give null. */
export function nameNumber(name: string): string | null {
  const m = /^[A-Z]+-(\d+)$/.exec(name);
  return m ? String(Number(m[1])) : null;
}

const typeOfName = (g: Gear): string => g.name.replace(/-[^-]*$/, "") || g.type;

/** The top panel of a subpanel, or the gear itself. */
function rootOf(room: SiteRoom, g: Gear): Gear {
  return (g.parentId && room.gear.find((x) => x.id === g.parentId)) || g;
}

/**
 * Tap PNL right after a panel (Robert, 2026-10-27, 1:06 PM): offer PNL-1A, Subpanel Of PNL-01. The parent is the
 * panel just before: the last gear in the room, or its parent when that one is a subpanel. Null: add as today.
 */
export function subpanelOffer(room: SiteRoom, type: string): { parent: Gear; name: string } | null {
  if (type !== "PNL") return null;
  const last = room.gear[room.gear.length - 1];
  if (!last || last.type !== "PNL") return null;
  const parent = rootOf(room, last);
  const n = nameNumber(parent.name);
  if (!n) return null;
  const used = new Set(room.gear.map((g) => g.name));
  for (let i = 0; i < 26; i++) {
    const name = `${typeOfName(parent)}-${n}${String.fromCharCode(65 + i)}`;
    if (!used.has(name)) return { parent, name };
  }
  return null;
}

/** Adds the subpanel the offer names, right after its parent's last subpanel. */
export function addSubpanel(room: SiteRoom, parentId: string, id = newGearId()): Gear | null {
  const parent = room.gear.find((g) => g.id === parentId);
  if (!parent) return null;
  const at = room.gear.findIndex((g) => g.id === parentId);
  const offer = subpanelOffer({ ...room, gear: room.gear.slice(0, at + 1 + kidsOf(room, parentId).length) }, parent.type);
  if (!offer) return null;
  const g = blankGear(parent.type, offer.name, id);
  g.parentId = parent.id;
  room.gear.splice(at + 1 + kidsOf(room, parentId).length, 0, g);
  return g;
}

export const kidsOf = (room: SiteRoom, id: string): Gear[] => room.gear.filter((g) => g.parentId === id);

/** The room as blocks: each top piece of gear with its subpanels right after it. Puts a stray subpanel back with its parent. */
function blocks(room: SiteRoom): Gear[][] {
  const tops = room.gear.filter((g) => !g.parentId || !room.gear.some((x) => x.id === g.parentId));
  return tops.map((t) => [t, ...kidsOf(room, t.id)]);
}

/**
 * Drag and drop (ORDER_DRAG_ROOM_CODE_B3_WORDS.md): puts the gear at place `to` among its peers, then names the room
 * in hanging order. A panel moves with its subpanels; a subpanel moves only among its parent's. False: nothing moved.
 */
export function dropGear(room: SiteRoom, id: string, to: number): boolean {
  const g = room.gear.find((x) => x.id === id);
  if (!g) return false;
  const bs = blocks(room);
  const parent = g.parentId ? room.gear.find((x) => x.id === g.parentId) : undefined;
  const list: Gear[] | Gear[][] = parent ? bs.find((x) => x[0] === parent)!.slice(1) : bs;
  const from = parent ? (list as Gear[]).indexOf(g) : (list as Gear[][]).findIndex((x) => x[0] === g);
  const at = Math.max(0, Math.min(to, list.length - 1));
  if (from < 0 || from === at) return false;
  const [moved] = list.splice(from, 1);
  (list as unknown[]).splice(at, 0, moved);
  if (parent) {
    const b = bs.find((x) => x[0] === parent)!;
    b.splice(1, b.length - 1, ...(list as Gear[]));
  }
  room.gear = bs.flat();
  nameInOrder(room);
  return true;
}

/**
 * Names follow the hanging order: each type counts 01 up from the top, subpanels take their panel's number and A, B
 * in order, MCCs keep the lockout rule. A name set with Rename stays, and the count skips it.
 */
export function nameInOrder(room: SiteRoom): void {
  room.gear = blocks(room).flat();
  const held = new Set(room.gear.filter((g) => g.named).map((g) => g.name));
  const count = new Map<string, number>();
  for (const b of blocks(room)) {
    const top = b[0]!;
    if (top.type === "MCC") continue;
    if (!top.named) {
      let n = (count.get(top.type) ?? 0) + 1;
      while (held.has(`${top.type}-${String(n).padStart(2, "0")}`)) n++;
      count.set(top.type, n);
      top.name = `${top.type}-${String(n).padStart(2, "0")}`;
    }
    const num = nameNumber(top.name);
    if (!num) continue;
    let k = 0;
    for (const kid of b.slice(1)) {
      if (kid.named) continue;
      let name = "";
      do name = `${kid.type}-${num}${String.fromCharCode(65 + k++)}`;
      while (held.has(name) && k < 26);
      kid.name = name;
    }
  }
  renameMccs(room);
}

/** What Rename takes: one or two digits, then one letter if wanted. "2" is 02. Null: not a part it takes. */
export function renamePart(raw: string): string | null {
  const m = /^(\d{1,2})([A-Z]?)$/.exec(raw.trim().toUpperCase());
  if (!m || Number(m[1]) < 1) return null;
  return m[2] ? `${Number(m[1])}${m[2]}` : String(Number(m[1])).padStart(2, "0");
}

/** Why Rename is grey, or null when the name is good to go. */
export function renameProblem(room: SiteRoom, id: string, raw: string): string | null {
  const g = room.gear.find((x) => x.id === id);
  if (!g) return "That gear is gone.";
  if (!raw.trim()) return "";
  const part = renamePart(raw);
  if (!part) return "Use a number, then one letter if you want: 2, 02, 2A.";
  const name = `${g.type}-${part}`;
  if (name === g.name) return "";
  if (room.gear.some((x) => x.id !== id && x.name === name)) return `${name} is already in ${room.name}.`;
  return null;
}

/** The safe rename (item 21): the code stays, the part changes. A panel takes its subpanels' numbers with it. */
export function renameGear(room: SiteRoom, id: string, raw: string): boolean {
  const g = room.gear.find((x) => x.id === id);
  if (!g || g.type === "MCC" || renameProblem(room, id, raw) !== null) return false;
  g.name = `${g.type}-${renamePart(raw)}`;
  g.named = true;
  const n = nameNumber(g.name);
  if (n) for (const k of kidsOf(room, id)) k.name = k.name.replace(/-\d+([A-Z])$/, `-${n}$1`);
  return true;
}

// ---- Section letters ----

/** MCCs share one run of letters in a room. Switchboards and switchgear each start at A (Robert, 2026-10-23, 1:07 PM). */
export function roomLetters(room: SiteRoom): Map<string, string[]> {
  const out = new Map<string, string[]>();
  let run = 0;
  for (const g of room.gear) {
    if (!isSec(g)) continue;
    const start = g.type === "MCC" ? run : 0;
    out.set(g.id, g.sections.map((_, k) => letterOf(start + k)));
    if (g.type === "MCC") run += g.sections.length;
  }
  return out;
}

/** The letter the next section of this gear would get. Null past Z. */
export function nextLetter(room: SiteRoom, id: string): string | null {
  const g = room.gear.find((x) => x.id === id);
  if (!g) return null;
  let start = 0;
  if (g.type === "MCC") for (const x of room.gear) {
    if (x === g) break;
    if (x.type === "MCC") start += x.sections.length;
  }
  const i = start + g.sections.length;
  return i < 26 ? letterOf(i) : null;
}

// ---- Sections: taps on the grid paper ----

/** A tap on a gray square: the section fills down to it. A gray square in the Next column starts a new section. */
export function fillTo(g: Gear, section: number, space: number): void {
  if (section >= g.sections.length) g.sections.push(space);
  else if (space > g.sections[section]!) g.sections[section] = space;
}

/** End Section Here: the space tapped is the last. Splits and spares below it come out. */
export function endSectionAt(g: Gear, section: number, space: number): void {
  g.sections[section] = space;
  const keep = (k: string) => {
    const [s, p] = k.split(":").map(Number);
    return s !== section || p! <= space;
  };
  g.splits = Object.fromEntries(Object.entries(g.splits).filter(([k]) => keep(k)));
  g.spares = g.spares.filter(keep);
}

/** Clear Section: every space in it comes out, and the sections after it move up a letter with their splits and spares. */
export function clearSection(g: Gear, section: number): void {
  g.sections.splice(section, 1);
  const move = (k: string): string | null => {
    const [s, p] = k.split(":").map(Number);
    if (s === section) return null;
    return spaceKey(s! > section ? s! - 1 : s!, p!);
  };
  const splits: Record<string, number> = {};
  for (const [k, n] of Object.entries(g.splits)) {
    const m = move(k);
    if (m) splits[m] = n;
  }
  g.splits = splits;
  g.spares = g.spares.map(move).filter((x): x is string => Boolean(x));
  if (g.covers) {
    const covers: Record<string, [number, number]> = {};
    for (const [k, v] of Object.entries(g.covers)) {
      const i = Number(k);
      if (i !== section) covers[String(i > section ? i - 1 : i)] = v;
    }
    if (Object.keys(covers).length) g.covers = covers;
    else delete g.covers;
  }
}

/** Split Into Two, Three or Four. 1 puts it back as one. */
export function setSplit(g: Gear, section: number, space: number, ways: number): void {
  const k = spaceKey(section, space);
  if (ways > 1) g.splits[k] = Math.min(4, ways);
  else delete g.splits[k];
}

export function toggleSpare(g: Gear, section: number, space: number): void {
  const k = spaceKey(section, space);
  const i = g.spares.indexOf(k);
  if (i >= 0) g.spares.splice(i, 1);
  else g.spares.push(k);
}

/** "C5a-d": what a split square shows. */
export const splitRange = (position: string, ways: number): string => `${position}a-${SPLIT_LETTERS[ways - 1]}`;

// ---- Panels: a circle per circuit ----

/**
 * Breakers are counted the way the panel is walked and hung: down the left side, then down the right
 * (Robert, 2026-10-23, 3:08 PM; mockup MOCKUP_FIELD_LABELS_PANEL_COUNT.html approved 3:17 PM).
 * A 2-pole on 1 and 3 is B1, a 3-pole on 5, 7, 9 is B2, a single on 11 is B3. Spaces never take a number.
 * The gear door walks a panel the same way (survey-gear.ts panelWalk). Each breaker still keeps its circuits
 * behind the name, so the panel record and the schedule know B2 is circuits 5·7·9.
 */
export function breakerOrder(g: Gear): number[] {
  const tops = Object.keys(g.circuits).map(Number);
  return [...tops.filter((t) => t % 2).sort((a, b) => a - b), ...tops.filter((t) => t % 2 === 0).sort((a, b) => a - b)];
}

/** Each breaker's printed name, by its top circuit: B1, B2 ... in walk order. A main in the grid reads MB. */
export function breakerNames(g: Gear): Map<number, string> {
  let n = 0;
  return new Map(breakerOrder(g).map((t) => [t, isGridMain(g, t) ? "MB" : `B${++n}`]));
}

// ---- The main in the grid (MAIN_IN_GRID_B3_WORDS.md) ----

export const isGridMain = (g: Pick<Gear, "main" | "mainCircuit">, top: number): boolean => g.main && g.mainCircuit === top;

/** Main Breaker in the breaker's menu: this breaker is the main. One main per panel, so the top or bottom one goes. */
export function setGridMain(g: Gear, top: number): void {
  if (!g.circuits[String(top)]) return;
  g.main = true;
  g.mainCircuit = top;
  delete g.mainAt;
  dropTandem(g, top);
}

/** Not The Main: the breaker is a plain breaker again, and the panel has no main. */
export function notTheMain(g: Gear): void {
  g.main = false;
  delete g.mainCircuit;
}

/** Main Breaker On or Off in the row. Off takes a main in the grid with it; On puts the main at the top, as before. */
export function toggleMain(g: Gear): void {
  if (g.main) notTheMain(g);
  else g.main = true;
}

/** A main whose breaker is gone (cleared, made a Blank, or off a smaller panel) goes with it. */
function dropLostMain(g: Gear): void {
  if (g.mainCircuit !== undefined && !g.circuits[String(g.mainCircuit)]) notTheMain(g);
}

/** The circuits a breaker takes down its side: "7·9", "13·15·17", "7". */
export const circuitsOf = (top: number, poles: number): string => Array.from({ length: poles }, (_, k) => top + 2 * k).join("·");

/** The breaker a circuit belongs to: its top circuit. Null for a space. */
export function ownerOf(g: Gear, n: number): number | null {
  for (const [t, p] of Object.entries(g.circuits)) {
    const top = Number(t);
    if (n >= top && n <= top + 2 * (p - 1) && (n - top) % 2 === 0) return top;
  }
  return null;
}

/** A pole count fits when every circuit it takes down the same side is on the panel and free. */
export function polesFit(g: Gear, top: number, poles: number): boolean {
  for (let k = 1; k < poles; k++) {
    const c = top + 2 * k;
    if (c > g.panelSize || isBlank(g, c)) return false;
    const o = ownerOf(g, c);
    if (o !== null && o !== top) return false;
  }
  return true;
}

/** A tap on a gray circle: a 1-pole breaker there. A tap on a circle another breaker or a Blank takes does nothing. */
export function addBreaker(g: Gear, n: number): void {
  if (ownerOf(g, n) === null && !isBlank(g, n) && n >= 1 && n <= g.panelSize) g.circuits[String(n)] = 1;
}

// ---- Blank on panels (F3a, PANEL_MODEL_B1_WORDS.md section 3) ----

export const isBlank = (g: Pick<Gear, "blanks">, n: number): boolean => !!g.blanks?.includes(n);

/** Blank: the 1-pole breaker at n becomes a filler. A 2 or 3 pole is set back to its top circuit first. */
export function setBlank(g: Gear, n: number): void {
  const top = ownerOf(g, n);
  if (top !== null) {
    delete g.circuits[String(top)];
    dropTandem(g, top);
    dropLostMain(g);
  }
  g.blanks = [...new Set([...(g.blanks ?? []), n])].sort((a, b) => a - b);
}

/** Make It A Breaker (to a 1-pole) or Clear (to a space not walked). */
export function unBlank(g: Gear, n: number, breaker: boolean): void {
  const left = (g.blanks ?? []).filter((x) => x !== n);
  if (left.length) g.blanks = left;
  else delete g.blanks;
  if (breaker) addBreaker(g, n);
}

/**
 * The plain 1-pole breakers a bigger pole count would take in (FIELD_TEST_NICHOLASVILLE_1028.md item 2, Robert
 * 3:19 PM): a 3-pole main on 2 takes in the breakers on 4 and 6. Empty when the circuits are free. Null when
 * something else is in the way: the panel's end, a Blank, a tandem, a main, or a 2 or 3 pole breaker.
 */
export function polesTakeIn(g: Gear, top: number, poles: number): number[] | null {
  const take: number[] = [];
  for (let k = 1; k < poles; k++) {
    const c = top + 2 * k;
    if (c > g.panelSize || isBlank(g, c)) return null;
    const o = ownerOf(g, c);
    if (o === null || o === top) continue;
    if (o !== c || g.circuits[String(c)] !== 1 || isTandem(g, c) || isGridMain(g, c)) return null;
    take.push(c);
  }
  return take;
}

/** Poles on a breaker. Plain 1-pole breakers in the way are taken in (polesTakeIn); Undo puts them back. */
export function setPoles(g: Gear, top: number, poles: number): void {
  const take = g.circuits[String(top)] ? polesTakeIn(g, top, poles) : null;
  if (!take) return;
  for (const c of take) delete g.circuits[String(c)];
  g.circuits[String(top)] = poles;
  if (poles > 1) dropTandem(g, top);
}

export function clearBreaker(g: Gear, top: number): void {
  delete g.circuits[String(top)];
  dropTandem(g, top);
  dropLostMain(g);
}

// ---- Tandems (Robert, 2026-10-27, 9:24 PM) ----

export const isTandem = (g: Pick<Gear, "tandems">, top: number): boolean => !!g.tandems?.includes(top);

function dropTandem(g: Gear, top: number): void {
  if (!g.tandems) return;
  const left = g.tandems.filter((x) => x !== top);
  if (left.length) g.tandems = left;
  else delete g.tandems;
}

/** Tandem: a 1-pole position only. Sets it, or takes it back to a plain 1-pole. */
export function toggleTandem(g: Gear, top: number): void {
  if (g.circuits[String(top)] !== 1 || isGridMain(g, top)) return;
  if (isTandem(g, top)) dropTandem(g, top);
  else g.tandems = [...(g.tandems ?? []), top].sort((a, b) => a - b);
}

/** A smaller panel drops the breakers that no longer fit on it. */
/**
 * Panel size. A smaller panel sets aside the breakers and Blanks that no longer fit, with their tandem marks and
 * the main; a bigger one brings back each one that fits again in a space still free. Nothing walked is lost.
 */
export function setPanelSize(g: Gear, size: number): void {
  g.panelSize = size;
  const park = g.parked ?? { circuits: {}, tandems: [], blanks: [] };
  // Bring back what fits now, where nothing has been walked since.
  for (const [t, p] of Object.entries(park.circuits)) {
    const top = Number(t);
    if (top + 2 * (p - 1) > size) continue;
    const free = Array.from({ length: p }, (_, k) => top + 2 * k).every((c) => ownerOf(g, c) === null && !isBlank(g, c));
    if (!free) continue;
    g.circuits[t] = p;
    delete park.circuits[t];
    if (park.tandems.includes(top)) {
      g.tandems = [...new Set([...(g.tandems ?? []), top])].sort((a, b) => a - b);
      park.tandems = park.tandems.filter((x) => x !== top);
    }
    if (park.mainCircuit === top && !(g.main && g.mainCircuit !== undefined)) {
      g.main = true;
      g.mainCircuit = top;
      delete g.mainAt;
      delete park.mainCircuit;
    }
  }
  for (const n of [...park.blanks]) {
    if (n > size || ownerOf(g, n) !== null || isBlank(g, n)) continue;
    g.blanks = [...new Set([...(g.blanks ?? []), n])].sort((a, b) => a - b);
    park.blanks = park.blanks.filter((x) => x !== n);
  }
  // Set aside what no longer fits.
  for (const [t, p] of Object.entries(g.circuits))
    if (Number(t) + 2 * (p - 1) > size) {
      park.circuits[t] = p;
      if (isTandem(g, Number(t))) park.tandems.push(Number(t));
      if (isGridMain(g, Number(t))) park.mainCircuit = Number(t);
      delete g.circuits[t];
      dropTandem(g, Number(t));
    }
  dropLostMain(g);
  if (g.blanks) {
    park.blanks.push(...g.blanks.filter((n) => n > size));
    const keep = g.blanks.filter((n) => n <= size);
    if (keep.length) g.blanks = keep;
    else delete g.blanks;
  }
  if (Object.keys(park.circuits).length || park.blanks.length) g.parked = { ...park, tandems: [...new Set(park.tandems)], blanks: [...new Set(park.blanks)] };
  else delete g.parked;
}

// ---- Covers on sectioned gear (decision 1, PANEL_MODEL_B1_WORDS.md section 2) ----

export const MAX_COVERS = 3;

/** [on top, at bottom] for a section. */
export const coversOf = (g: Pick<Gear, "covers">, section: number): [number, number] => g.covers?.[String(section)] ?? [0, 0];

/** Covers On Top or Covers At Bottom: 0 to 3. Bucket numbers never move. */
export function setCovers(g: Gear, section: number, where: "top" | "bottom", n: number): void {
  const [t, b] = coversOf(g, section);
  const next: [number, number] = where === "top" ? [clampCovers(n), b] : [t, clampCovers(n)];
  const all = { ...(g.covers ?? {}) };
  if (next[0] || next[1]) all[String(section)] = next;
  else delete all[String(section)];
  if (Object.keys(all).length) g.covers = all;
  else delete g.covers;
}

const clampCovers = (n: number): number => Math.max(0, Math.min(MAX_COVERS, Math.floor(n) || 0));

export function breakerTops(g: Gear): number[] {
  return Object.keys(g.circuits).map(Number).sort((a, b) => a - b);
}

// ---- Labels ----

/** A label with where it lives on the gear, so the preview can go straight to it. */
export interface SiteLabel extends FieldLabel {
  gearId: string;
  /** Sectioned gear: section (0 up) and space (1 up). */
  sec?: number;
  space?: number;
  /** Panels: the breaker's top circuit. */
  circuit?: number;
  /** Panels: which half of a tandem, A on top or B below. */
  half?: "A" | "B";
}

/** The labels on one piece of gear, in hanging order: its own label, then section by section, top to bottom. */
export function gearLabels(room: SiteRoom, g: Gear, letters = roomLetters(room)): SiteLabel[] {
  const base = { room: room.name, housing: g.name, gearId: g.id };
  const out: SiteLabel[] = [{ ...base, key: `${room.name}|${g.name}|`, text: g.name, kind: BIG_TYPES.includes(g.type) ? "big" : "housing", section: null, spare: false }];
  if (isSec(g)) {
    const ls = letters.get(g.id) ?? [];
    g.sections.forEach((count, si) => {
      for (let k = 1; k <= count; k++) {
        const p = `${ls[si] ?? "?"}${k}`;
        const ways = g.splits[spaceKey(si, k)];
        const spare = g.spares.includes(spaceKey(si, k));
        const texts = ways ? SPLIT_LETTERS.slice(0, ways).map((x) => p + x) : [p];
        for (const t of texts) out.push({ ...base, key: `${room.name}|${g.name}|${t}`, text: t, kind: "bucket", section: si + 1, spare, sec: si, space: k });
      }
    });
  } else if (isPanel(g)) {
    // The main always prints (Robert, 2026-10-27, 9:19 PM). B1 to Bn print only with Breaker Labels On.
    const mb: SiteLabel = { ...base, key: `${room.name}|${g.name}|MB`, text: "MB", kind: "breaker", section: null, spare: false };
    const inGrid = g.main && g.mainCircuit !== undefined;
    // Main At Bottom prints MB after the last breaker, the order the labels go on walking down (decision 1).
    if (g.main && !inGrid && g.mainAt !== "bottom") out.push(mb);
    const names = breakerNames(g);
    for (const top of breakerOrder(g)) {
      // A main in the grid prints in its place in the walk, Breaker Labels On or Off (B3).
      if (isGridMain(g, top)) {
        out.push({ ...mb, circuit: top });
        continue;
      }
      if (!g.breakerLabels) continue;
      const t = names.get(top)!;
      // A tandem prints two labels on one number: B5A on top, B5B below.
      if (isTandem(g, top))
        for (const half of ["A", "B"] as const) out.push({ ...base, key: `${room.name}|${g.name}|${t}${half}`, text: `${t}${half}`, kind: "breaker", section: null, spare: false, circuit: top, half });
      else out.push({ ...base, key: `${room.name}|${g.name}|${t}`, text: t, kind: "breaker", section: null, spare: false, circuit: top });
    }
    if (g.main && !inGrid && g.mainAt === "bottom") out.push(mb);
  }
  return out;
}

export interface SiteLineup extends Lineup {
  labels: SiteLabel[];
  /** Size picks from the gear, in the engine's shape. */
  choices: SizeChoices;
  /** Gear marked Shock Only, in walk order: it needs a shock label and prints no field label. */
  shock: { room: string; name: string; gearId: string }[];
}

/** The Shock Only switch on the gear card. */
export function toggleShockOnly(g: Gear): void {
  if (g.shockOnly) delete g.shockOnly;
  else g.shockOnly = true;
}

/**
 * The site in the engine's shape, so sizes, the fit rule, print files and the PDF work as they did.
 * Problems: a room past Z on its section letters (red), and labels past the 10% shrink (amber).
 */
export function siteLineup(site: FieldSite): SiteLineup {
  const rooms: Room[] = [];
  const labels: SiteLabel[] = [];
  const choices: SizeChoices = {};
  const problems: Problem[] = [];
  const shock: SiteLineup["shock"] = [];
  for (const r of site.rooms) {
    const letters = roomLetters(r);
    const housings: Housing[] = [];
    for (const g of r.gear) {
      if (g.shockOnly) {
        shock.push({ room: r.name, name: g.name, gearId: g.id });
        continue;
      }
      const all = gearLabels(r, g, letters);
      const [own, ...kids] = all;
      if (g.size && sizeFromKey(g.size)) choices[own!.key] = g.size;
      if (g.kidSize && sizeFromKey(g.kidSize)) choices[kidsKey(r.name, g.name)] = g.kidSize;
      if (all.some((l) => l.text.startsWith("?"))) problems.push({ tone: "red", line: 0, key: own!.key, text: `${r.name} has run out of section letters on ${g.name}. Take a section off.` });
      housings.push({ room: r.name, name: g.name, type: g.type, label: own!, kids, lineSize: null, main: g.main, line: 0, lastLine: 0 });
      labels.push(...all);
    }
    rooms.push({ name: r.name, housings, line: 1 });
  }
  const lineup: SiteLineup = { rooms, labels, problems, choices, shock };
  lineup.problems = [...problems, ...fitProblems(lineup, choices)];
  return lineup;
}

/**
 * One room's labels, for printing room by room (Robert, 2026-10-23, 3:31 PM: option 1, a room picker in the preview).
 * Empty room name: the whole site.
 */
export function roomLineup(l: SiteLineup, room: string): SiteLineup {
  if (!room) return l;
  return { ...l, rooms: l.rooms.filter((r) => r.name === room), labels: l.labels.filter((x) => x.room === room), problems: l.problems.filter((p) => !p.key || p.key.startsWith(`${room}|`)) };
}

/** "6 buckets", "4 breakers", "One label". */
export function gearSummary(room: SiteRoom, g: Gear): string {
  if (g.shockOnly) return "No field label";
  if (isSec(g)) {
    const n = gearLabels(room, g).length - 1;
    const spares = g.spares.length;
    const covers = Object.values(g.covers ?? {}).reduce((a, [t, b]) => a + t + b, 0);
    return `${n} ${g.type === "MCC" ? (n === 1 ? "bucket" : "buckets") : n === 1 ? "compartment" : "compartments"}${spares ? ` · ${spares} spare` : ""}${covers ? ` · ${covers} cover${covers === 1 ? "" : "s"}` : ""}`;
  }
  if (isPanel(g)) {
    const n = breakerTops(g).filter((t) => !isGridMain(g, t)).length;
    const off = !g.breakerLabels && n > 0;
    const blanks = g.blanks?.length ?? 0;
    const tandems = g.tandems?.length ?? 0;
    // No main: a main lug only panel. Said once breakers are walked, so a new panel never claims it (B3, 9:05 AM).
    return `${n} breaker${n === 1 ? "" : "s"}${g.main ? " with main" : n ? " · MLO" : ""}${tandems ? ` · ${tandems} tandem` : ""}${blanks ? ` · ${blanks} blank` : ""}${off ? " · breakers not printed" : ""}`;
  }
  return "One label";
}

export function labelCount(site: FieldSite): number {
  return site.rooms.reduce((a, r) => {
    const letters = roomLetters(r);
    return a + r.gear.reduce((b, g) => b + gearLabels(r, g, letters).length, 0);
  }, 0);
}

// ---- Saved on this iPad ----

/** Reads a saved site, dropping anything that is not the right shape. */
export function readSite(raw: unknown): FieldSite {
  const out: FieldSite = { rooms: [] };
  const rooms = (raw as { rooms?: unknown })?.rooms;
  if (!Array.isArray(rooms)) return out;
  for (const r of rooms) {
    if (!r || typeof r.name !== "string" || !Array.isArray(r.gear)) continue;
    const gear: Gear[] = [];
    for (const g of r.gear as Partial<Gear>[]) {
      if (!g || typeof g.type !== "string" || typeof g.name !== "string") continue;
      // Manual Motor Starter is MS, as the Lists page has it (Robert, 2026-10-29 9:40 AM). A walk that typed it MMS
      // reads MS from here on, name and all: MMS-01 is MS-01. Its field label reads CHANGED until it is reprinted.
      const mms = g.type === "MMS";
      gear.push({
        id: typeof g.id === "string" && g.id ? g.id : newGearId(),
        type: mms ? "MS" : g.type,
        name: mms ? g.name.replace(/^MMS(?=-|$)/, "MS") : g.name,
        sections: Array.isArray(g.sections) ? g.sections.filter((n) => Number.isInteger(n) && n > 0) : [],
        splits: g.splits && typeof g.splits === "object" ? Object.fromEntries(Object.entries(g.splits).filter(([, n]) => Number.isInteger(n) && n >= 2 && n <= 4)) : {},
        spares: Array.isArray(g.spares) ? g.spares.filter((x) => typeof x === "string") : [],
        circuits: g.circuits && typeof g.circuits === "object" ? Object.fromEntries(Object.entries(g.circuits).filter(([, n]) => Number.isInteger(n) && n >= 1 && n <= 3)) : {},
        panelSize: PANEL_SIZES.includes(Number(g.panelSize)) ? Number(g.panelSize) : DEFAULT_PANEL_SIZE,
        main: g.main === true,
        ...(g.breakerLabels === true ? { breakerLabels: true } : {}),
        ...(typeof g.parentId === "string" && g.parentId ? { parentId: g.parentId } : {}),
        ...(typeof g.tag === "string" && cleanText(g.tag, GEAR_TAG_MAX) ? { tag: cleanText(g.tag, GEAR_TAG_MAX) } : {}),
        ...(g.named === true ? { named: true as const } : {}),
        ...(g.shockOnly === true ? { shockOnly: true as const } : {}),
        ...readParked(g.parked),
        ...(g.mainAt === "bottom" ? { mainAt: "bottom" as const } : {}),
        ...(g.main === true && Number.isInteger(g.mainCircuit) && g.circuits && typeof g.circuits === "object" && (g.circuits as Record<string, unknown>)[String(g.mainCircuit)]
          ? { mainCircuit: g.mainCircuit }
          : {}),
        ...readBlanks(g.blanks),
        ...readTandems(g.tandems, g.circuits),
        ...readCovers(g.covers),
        size: typeof g.size === "string" ? g.size : "",
        kidSize: typeof g.kidSize === "string" ? g.kidSize : "",
        ...readPhoto(g.photo),
      });
    }
    // A subpanel whose parent is not in the room reads as a plain panel.
    for (const g of gear) if (g.parentId && !gear.some((x) => x.id === g.parentId && x !== g)) delete g.parentId;
    const description = typeof r.description === "string" ? cleanText(r.description, ROOM_DESCRIPTION_MAX) : "";
    out.rooms.push({ name: r.name, gear, ...(r.done === true ? { done: true } : {}), ...(description ? { description } : {}), ...readPhoto(r.photo) });
  }
  sortRooms(out);
  const printed = readPrinted((raw as { printed?: unknown })?.printed);
  if (Object.keys(printed).length) out.printed = printed;
  const sent = readSent((raw as { sent?: unknown })?.sent);
  if (Object.keys(sent).length) out.sent = sent;
  const pushed = readPushed((raw as { pushed?: unknown })?.pushed);
  if (Object.keys(pushed).length) out.pushed = pushed;
  return out;
}

function readPushed(raw: unknown): Record<string, PushMark> {
  const out: Record<string, PushMark> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const m = v as { at?: unknown; ids?: unknown } | null;
    if (!m || typeof m.at !== "string" || !Array.isArray(m.ids)) continue;
    out[k] = { at: m.at, ids: m.ids.filter((x): x is string => typeof x === "string") };
  }
  return out;
}

/** The walk as this iPad stores it and the cloud keeps it: rooms and the marks no Undo takes back. */
export function walkMarks(site: FieldSite): { rooms: SiteRoom[]; printed: Record<string, PrintedMark>; sent: Record<string, string[]>; pushed: Record<string, PushMark> } {
  return { rooms: site.rooms, printed: site.printed ?? {}, sent: site.sent ?? {}, pushed: site.pushed ?? {} };
}

/**
 * A room pushed (PUSH_BY_ROOM_B7_WORDS.md item 8): its mark now holds the Asset IDs it makes, at this time.
 * Marks for rooms no longer in the walk go: their rows rode along with this push (item 10).
 */
export function markPushed(site: FieldSite, room: string, ids: readonly string[], at: string): FieldSite {
  const names = new Set(site.rooms.map((r) => r.name));
  const kept = Object.fromEntries(Object.entries(site.pushed ?? {}).filter(([k]) => names.has(k) && k !== room));
  return { ...site, pushed: { ...kept, [room]: { at, ids: [...ids] } } };
}

/** Rows taken out of the run before they saved (Clear The New Row): they are not on the sheet, so no pill. */
export function unmarkPushed(site: FieldSite, ids: readonly string[]): FieldSite {
  if (!site.pushed || !ids.length) return site;
  const out = new Set(ids);
  return { ...site, pushed: Object.fromEntries(Object.entries(site.pushed).map(([k, m]) => [k, { at: m.at, ids: m.ids.filter((id) => !out.has(id)) }])) };
}

/** "ER1" for 00-ER1-PNL-01, "EA2" for 00-EA2-ENCL-01, "Outside" for 00-UTL-01: the walk room an Asset ID sits in. */
export function roomOfAssetId(id: string): string {
  const seg = id.split("-")[1] ?? "";
  return /^(ER|EA)\d+[A-Z]?$/.test(seg) ? seg : OUTSIDE;
}

function readPhoto(raw: unknown): { photo?: WalkPhoto } {
  if (!raw || typeof raw !== "object") return {};
  const p = raw as Record<string, unknown>;
  if (typeof p.id !== "string" || !p.id || typeof p.path !== "string" || !p.path || typeof p.at !== "string" || typeof p.sent !== "string") return {};
  return { photo: { id: p.id, at: p.at, path: p.path, sent: p.sent } };
}

// ---- Room and gear photos (ROOM_GEAR_PHOTO_B5_WORDS.md, mockup approved 2026-10-29 5:19 AM) ----

/** The newer of two photos. */
function newer(a: WalkPhoto | undefined, b: WalkPhoto | undefined): WalkPhoto | undefined {
  if (!a) return b;
  if (!b) return a;
  return b.at > a.at ? b : a;
}

/**
 * Undo and Cancel put the walk back, never a photo: taking one is not an edit, the same as printing.
 * First, a photo `to` already holds takes its latest send from `from` (by photo id), so an Undo of a re-code
 * sends it back under the old name. Then each room keeps the newer photo of its name, each gear of its id.
 */
export function carryPhotos(from: FieldSite, to: FieldSite): void {
  const all = (s: FieldSite) => s.rooms.flatMap((r) => [r.photo, ...r.gear.map((g) => g.photo)]).filter((p): p is WalkPhoto => Boolean(p));
  const latest = new Map(all(from).map((p) => [p.id, p]));
  for (const r of to.rooms) {
    if (r.photo && latest.has(r.photo.id)) r.photo = latest.get(r.photo.id);
    for (const g of r.gear) if (g.photo && latest.has(g.photo.id)) g.photo = latest.get(g.photo.id);
  }
  const placed = new Set(all(to).map((p) => p.id));
  const take = (mine: WalkPhoto | undefined, theirs: WalkPhoto | undefined) => (theirs && !placed.has(theirs.id) ? newer(mine, theirs) : mine);
  const rooms = new Map(from.rooms.map((r) => [r.name, r.photo]));
  const gear = new Map(from.rooms.flatMap((r) => r.gear.map((g) => [g.id, g.photo] as const)));
  for (const r of to.rooms) {
    const p = take(r.photo, rooms.get(r.name));
    if (p && p !== r.photo) {
      r.photo = p;
      placed.add(p.id);
    }
    for (const g of r.gear) {
      const q = take(g.photo, gear.get(g.id));
      if (q && q !== g.photo) {
        g.photo = q;
        placed.add(q.id);
      }
    }
  }
}

/** A photo whose room or gear name moved since it went: send it again under the name it has now. */
export interface PhotoBehind {
  room: string;
  /** The gear, or "" for the room photo. */
  gearId: string;
  photo: WalkPhoto;
  /** The Drive name it should have now. */
  want: string;
}

export function photosBehind(site: FieldSite, siteNumber: string): PhotoBehind[] {
  const out: PhotoBehind[] = [];
  for (const r of site.rooms) {
    if (r.photo) {
      const want = photoDriveName(siteNumber, r.name);
      if (want !== r.photo.sent) out.push({ room: r.name, gearId: "", photo: r.photo, want });
    }
    for (const g of r.gear) {
      if (!g.photo) continue;
      const want = photoDriveName(siteNumber, r.name, g.name);
      if (want !== g.photo.sent) out.push({ room: r.name, gearId: g.id, photo: g.photo, want });
    }
  }
  return out;
}

function readSent(raw: unknown): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) if (Array.isArray(v)) out[k] = v.filter((x): x is string => typeof x === "string");
  return out;
}

/** The order a Drive PDF holds, kept when Update PDF makes it. */
export function markSent(site: FieldSite, roll: string, labels: readonly SiteLabel[]): FieldSite {
  return { ...site, sent: { ...(site.sent ?? {}), [roll]: labels.map(printIdOf) } };
}

/**
 * True when labels the Drive PDF already holds now sit in another order. Labels added or taken out since are
 * left out of the check: they already show as waiting. No saved order (a PDF made before this): false.
 */
export function orderMoved(sent: readonly string[] | undefined, labels: readonly SiteLabel[]): boolean {
  if (!sent) return false;
  const now = labels.map(printIdOf);
  const was = new Set(sent);
  const here = new Set(now);
  const a = sent.filter((id) => here.has(id));
  const b = now.filter((id) => was.has(id));
  return a.some((id, i) => id !== b[i]);
}

/** Start Printing Over: every label reads NEW again. The walk itself stays as it is. */
export function startPrintingOver(site: FieldSite): FieldSite {
  const out = { ...site };
  delete out.printed;
  delete out.sent;
  return out;
}

function readParked(raw: unknown): { parked?: Gear["parked"] } {
  if (!raw || typeof raw !== "object") return {};
  const r = raw as Record<string, unknown>;
  const circuits = r.circuits && typeof r.circuits === "object" ? Object.fromEntries(Object.entries(r.circuits as Record<string, unknown>).filter(([k, n]) => Number(k) >= 1 && Number.isInteger(n) && (n as number) >= 1 && (n as number) <= 3)) as Record<string, number> : {};
  const nums = (v: unknown) => (Array.isArray(v) ? v.filter((n): n is number => Number.isInteger(n) && n >= 1 && n <= 84) : []);
  const blanks = nums(r.blanks);
  if (!Object.keys(circuits).length && !blanks.length) return {};
  return { parked: { circuits, tandems: nums(r.tandems), blanks, ...(Number.isInteger(r.mainCircuit) ? { mainCircuit: r.mainCircuit as number } : {}) } };
}

function readBlanks(raw: unknown): { blanks?: number[] } {
  if (!Array.isArray(raw)) return {};
  const b = [...new Set(raw.filter((n): n is number => Number.isInteger(n) && n >= 1 && n <= 84))].sort((x, y) => x - y);
  return b.length ? { blanks: b } : {};
}

/** Tandems kept only on 1-pole breakers that are there. */
function readTandems(raw: unknown, circuits: unknown): { tandems?: number[] } {
  if (!Array.isArray(raw) || !circuits || typeof circuits !== "object") return {};
  const c = circuits as Record<string, unknown>;
  const t = [...new Set(raw.filter((n): n is number => Number.isInteger(n) && c[String(n)] === 1))].sort((x, y) => x - y);
  return t.length ? { tandems: t } : {};
}

function readCovers(raw: unknown): { covers?: Record<string, [number, number]> } {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, [number, number]> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!/^\d+$/.test(k) || !Array.isArray(v) || v.length !== 2) continue;
    const t = clampCovers(Number(v[0]));
    const b = clampCovers(Number(v[1]));
    if (t || b) out[k] = [t, b];
  }
  return Object.keys(out).length ? { covers: out } : {};
}

function readPrinted(raw: unknown): Record<string, PrintedMark> {
  const out: Record<string, PrintedMark> = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [id, m] of Object.entries(raw as Record<string, unknown>)) {
    const v = m as Partial<PrintedMark> | null;
    if (v && typeof v.text === "string" && typeof v.at === "string") out[id] = { text: v.text, at: v.at };
  }
  return out;
}

// ---- What printed (ONE_PRINT_WORDS.md, approved 2026-10-24) ----

/**
 * A label's identity for printing: its gear and its place on the gear, never its name. Rename PANEL A to
 * PANEL 1A and the label reads CHANGED ("Was PANEL A"), not NEW. Move the gear to another room and nothing
 * changes, because the room doesn't print.
 */
export function printIdOf(l: SiteLabel): string {
  if (l.kind === "housing" || l.kind === "big") return `${l.gearId}|own`;
  if (l.kind === "bucket") return `${l.gearId}|${l.sec ?? 0}.${l.space ?? 0}${/[a-d]$/.test(l.text) ? l.text.slice(-1) : ""}`;
  if (l.circuit !== undefined) return `${l.gearId}|c${l.circuit}${l.half ?? ""}`;
  return `${l.gearId}|${l.text}`;
}

export type PrintState = { state: "new" } | { state: "changed"; was: string; at: string } | { state: "current"; at: string };

/** NEW: never printed. CHANGED: printed, and its text has moved since. Current: printed as it reads now. */
export function printStateOf(l: SiteLabel, printed: Record<string, PrintedMark> | undefined): PrintState {
  const m = printed?.[printIdOf(l)];
  if (!m) return { state: "new" };
  return m.text === l.text ? { state: "current", at: m.at } : { state: "changed", was: m.text, at: m.at };
}

/** Marks labels printed now (Option A, 12:47: a label counts as printed when Make PDF runs). */
export function markPrinted(site: FieldSite, labels: readonly SiteLabel[], at: string): FieldSite {
  const printed = { ...(site.printed ?? {}) };
  for (const l of labels) printed[printIdOf(l)] = { text: l.text, at };
  return { ...site, printed };
}

/**
 * The PR #128/#129 notepad, carried into the rev 2.8 record once, so a site already started is not lost.
 * Panels counted breakers B1 to Bn; they come over as 1-pole breakers on circuits 1 to n.
 */
export function siteFromNotepad(text: string, choices: SizeChoices = {}): FieldSite {
  const lineup = parseLineups(text);
  const site: FieldSite = { rooms: [] };
  for (const r of lineup.rooms) {
    if (r.name === "No Room") continue;
    const room: SiteRoom = { name: r.name, gear: [] };
    for (const h of r.housings) {
      const g = blankGear(h.type, h.name);
      g.size = choices[h.label.key] ?? "";
      g.kidSize = choices[kidsKey(r.name, h.name)] ?? "";
      if (isSectioned(h.type)) {
        g.sections = sectionCounts(h);
        const firstLetters = [...new Set(h.kids.map((k) => k.text[0]!))];
        for (const k of h.kids) {
          const m = /^([A-Z])(\d+)([a-z])?$/.exec(k.text);
          if (!m) continue;
          const key = spaceKey(firstLetters.indexOf(m[1]!), Number(m[2]));
          if (m[3]) g.splits[key] = Math.max(g.splits[key] ?? 0, SPLIT_LETTERS.indexOf(m[3] as (typeof SPLIT_LETTERS)[number]) + 1, 2);
          if (k.spare && !g.spares.includes(key)) g.spares.push(key);
        }
      } else if (BREAKER_TYPES.includes(h.type)) {
        g.main = h.main;
        const n = h.kids.filter((k) => k.text !== "MB").length;
        for (let c = 1; c <= n; c++) g.circuits[String(c)] = 1;
        // The notepad never knew 12, so its smallest panel stays 24 as it always carried over.
        g.panelSize = PANEL_SIZES.find((s) => s >= Math.max(n, 24)) ?? 84;
      }
      room.gear.push(g);
    }
    site.rooms.push(room);
  }
  sortRooms(site);
  return site;
}
